import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import RecordContributionForm from '../../components/forms/RecordContributionForm';
import RequestLoanForm from '../../components/forms/RequestLoanForm';
import ScheduleMeetingForm from '../../components/forms/ScheduleMeetingForm';
import { useFetch } from '../../hooks';
import ErrorHandler from '../../utils/errorHandler';
import { useToast } from '../../contexts/ToastContext';

const ChamaDetailEnhanced = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [tabData, setTabData] = useState({
    members: [],
    contributions: [],
    loans: [],
    meetings: [],
  });

  const [showContributionModal, setShowContributionModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  const {
    data: chama,
    loading,
    refetch: loadChama,
  } = useFetch(`/chamas/${id}`, {
    onError: (error) => {
      ErrorHandler.handleApiError(error, showToast, 'Failed to load chama');
    },
  });

  const loadTabData = async (tab) => {
    try {
      let endpoint = '';
      switch (tab) {
        case 'members':
          endpoint = `/chamas/${id}/members`;
          break;
        case 'contributions':
          endpoint = `/chamas/${id}/contributions`;
          break;
        case 'loans':
          endpoint = `/loans?chama_id=${id}`;
          break;
        case 'meetings':
          endpoint = `/meetings?chama_id=${id}`;
          break;
        default:
          return;
      }
      const response = await api.get(endpoint);

      const list = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      setTabData((prev) => ({
        ...prev,
        [tab]: list,
      }));
    } catch (error) {
      ErrorHandler.handleApiError(error, showToast, `Failed to load ${tab}`);
    }
  };

  useEffect(() => {
    if (activeTab !== 'overview' && activeTab !== 'settings') {
      loadTabData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleUpdateChama = async (updates) => {
    try {
      await api.put(`/chamas/${id}`, updates);
      loadChama();
    } catch (error) {
      showToast('Failed to update chama', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!chama) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="text-[#d8d2c5]">Chama not found</p>
          <Button className="mt-4" onClick={() => navigate('/chamas')}>
            Back to Chamas
          </Button>
        </div>
      </Card>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'members', label: 'Members', icon: '👥', count: tabData.members.length },
    {
      id: 'contributions',
      label: 'Contributions',
      icon: '💰',
      count: tabData.contributions.length,
    },
    { id: 'loans', label: 'Loans', icon: '💳', count: tabData.loans.length },
    { id: 'meetings', label: 'Meetings', icon: '📅', count: tabData.meetings.length },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex gap-4">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-3xl">{chama.name?.charAt(0) || 'C'}</span>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white">{chama.name}</h1>
              <Badge variant={chama.status === 'active' ? 'success' : 'neutral'}>
                {chama.status}
              </Badge>
            </div>
            <p className="text-[#d8d2c5]">{chama.description || 'No description'}</p>
            <div className="flex gap-4 mt-3 text-sm text-[#d8d2c5]">
              <span>👥 {chama.members_count || 0} members</span>
              <span>📅 Created {new Date(chama.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export
          </Button>
          <Button onClick={() => navigate(`/chamas/${id}/members`)}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Manage Members
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <Badge variant="neutral" size="sm">
                  {tab.count}
                </Badge>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          chama={chama}
          onAction={(action) => {
            if (action === 'contribution') setShowContributionModal(true);
            if (action === 'loan') setShowLoanModal(true);
            if (action === 'meeting') setShowMeetingModal(true);
          }}
        />
      )}

      {activeTab === 'members' && (
        <MembersTab
          members={tabData.members}
          chamaId={id}
          onRefresh={() => loadTabData('members')}
        />
      )}

      {activeTab === 'contributions' && (
        <ContributionsTab
          contributions={tabData.contributions}
          onRecord={() => setShowContributionModal(true)}
        />
      )}

      {activeTab === 'loans' && (
        <LoansTab loans={tabData.loans} onRequest={() => setShowLoanModal(true)} />
      )}

      {activeTab === 'meetings' && (
        <MeetingsTab meetings={tabData.meetings} onSchedule={() => setShowMeetingModal(true)} />
      )}

      {activeTab === 'settings' && <SettingsTab chama={chama} onUpdate={handleUpdateChama} />}

      {/* Modals */}
      <Modal
        isOpen={showContributionModal}
        onClose={() => setShowContributionModal(false)}
        title="Record Contribution"
        size="lg"
      >
        <RecordContributionForm
          chamaId={id}
          onSuccess={() => {
            setShowContributionModal(false);
            loadTabData('contributions');
          }}
          onCancel={() => setShowContributionModal(false)}
        />
      </Modal>

      <Modal
        isOpen={showLoanModal}
        onClose={() => setShowLoanModal(false)}
        title="Request Loan"
        size="lg"
      >
        <RequestLoanForm
          chamaId={id}
          onSuccess={() => {
            setShowLoanModal(false);
            loadTabData('loans');
          }}
          onCancel={() => setShowLoanModal(false)}
        />
      </Modal>

      <Modal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        title="Schedule Meeting"
        size="lg"
      >
        <ScheduleMeetingForm
          chamaId={id}
          onSuccess={() => {
            setShowMeetingModal(false);
            loadTabData('meetings');
          }}
          onCancel={() => setShowMeetingModal(false)}
        />
      </Modal>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ chama, onAction }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">Total Contributions</p>
            <p className="text-2xl font-bold text-gray-900">
              KES {(chama.total_contributions || 0).toLocaleString()}
            </p>
            <Badge variant="success" size="sm" className="mt-2">
              +8% this month
            </Badge>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">Active Loans</p>
            <p className="text-2xl font-bold text-gray-900">{chama.active_loans || 0}</p>
            <Badge variant="info" size="sm" className="mt-2">
              2 pending
            </Badge>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">Next Meeting</p>
            <p className="text-2xl font-bold text-gray-900">5</p>
            <Badge variant="warning" size="sm" className="mt-2">
              days away
            </Badge>
          </div>
        </Card>
      </div>

      <Card title="Quick Actions">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => onAction('contribution')}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <span className="text-sm font-semibold">Record Contribution</span>
          </button>
          <button
            type="button"
            onClick={() => onAction('loan')}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-sky-500 hover:bg-sky-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-sky-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <span className="text-sm font-semibold">Request Loan</span>
          </button>
          <button
            type="button"
            onClick={() => onAction('meeting')}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-amber-500 hover:bg-amber-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <span className="text-sm font-semibold">Schedule Meeting</span>
          </button>
        </div>
      </Card>
    </div>

    <div className="space-y-6">
      <Card title="Chama Details">
        <div className="space-y-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Contribution Frequency</p>
            <p className="text-sm font-semibold text-gray-900 capitalize">
              {chama.contribution_frequency || 'Monthly'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Contribution Amount</p>
            <p className="text-sm font-semibold text-gray-900">
              KES {(chama.contribution_amount || 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Meeting Day</p>
            <p className="text-sm font-semibold text-gray-900 capitalize">
              {chama.meeting_day || 'Not set'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Late Penalty</p>
            <p className="text-sm font-semibold text-gray-900">
              KES {(chama.late_penalty || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </Card>
    </div>
  </div>
);

// Members Tab Component
const MembersTab = ({ members, chamaId }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h3 className="text-lg font-semibold">{members.length} Members</h3>
      <Button
        size="sm"
        onClick={() => {
          window.location.href = `/chamas/${chamaId}/members`;
        }}
      >
        Manage Members
      </Button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {members.slice(0, 6).map((member) => (
        <Card key={member.id} padding={false}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {member.user?.name?.charAt(0) || 'M'}
                </span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{member.user?.name || 'Unknown'}</h4>
                <p className="text-xs text-gray-500">{member.role}</p>
              </div>
              <Badge variant={member.status === 'active' ? 'success' : 'neutral'} size="sm">
                {member.status}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-gray-500">Contributions</p>
                <p className="font-semibold">
                  KES {(member.total_contributions || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Loans</p>
                <p className="font-semibold">{member.active_loans || 0}</p>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  </div>
);

// Contributions Tab Component
const ContributionsTab = ({ contributions, onRecord }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h3 className="text-lg font-semibold">{contributions.length} Contributions</h3>
      <Button size="sm" onClick={onRecord}>
        Record Contribution
      </Button>
    </div>
    <div className="space-y-3">
      {contributions.slice(0, 10).map((contribution) => (
        <div
          key={contribution.id}
          className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">
                {contribution.member_name || 'Unknown'}
              </h4>
              <p className="text-xs text-gray-500">
                {new Date(contribution.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold text-gray-900">
              KES {(contribution.amount || 0).toLocaleString()}
            </p>
            <Badge variant="success" size="sm">
              Paid
            </Badge>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Loans Tab Component
const LoansTab = ({ loans, onRequest }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h3 className="text-lg font-semibold">{loans.length} Loans</h3>
      <Button size="sm" onClick={onRequest}>
        Request Loan
      </Button>
    </div>
    <div className="space-y-3">
      {loans.slice(0, 10).map((loan) => (
        <div
          key={loan.id}
          className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-sky-300 hover:bg-sky-50/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-sky-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{loan.member_name || 'Unknown'}</h4>
              <p className="text-xs text-gray-500">{loan.purpose || 'No purpose'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold text-gray-900">KES {(loan.amount || 0).toLocaleString()}</p>
            <Badge
              variant={(() => {
                if (loan.status === 'approved') return 'success';
                if (loan.status === 'pending') return 'warning';
                return 'info';
              })()}
              size="sm"
            >
              {loan.status}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Meetings Tab Component
const MeetingsTab = ({ meetings, onSchedule }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h3 className="text-lg font-semibold">{meetings.length} Meetings</h3>
      <Button size="sm" onClick={onSchedule}>
        Schedule Meeting
      </Button>
    </div>
    <div className="space-y-3">
      {meetings.slice(0, 10).map((meeting) => (
        <div
          key={meeting.id}
          className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex flex-col items-center justify-center text-amber-600">
              <span className="text-lg font-bold">{new Date(meeting.meeting_date).getDate()}</span>
              <span className="text-xs">
                {new Date(meeting.meeting_date).toLocaleDateString('en-US', { month: 'short' })}
              </span>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{meeting.title || 'Meeting'}</h4>
              <p className="text-xs text-gray-500">{meeting.location || 'No location'}</p>
            </div>
          </div>
          <Badge variant="info" size="sm">
            Scheduled
          </Badge>
        </div>
      ))}
    </div>
  </div>
);

// Settings Tab Component
const SettingsTab = ({ chama, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: chama.name || '',
    description: chama.description || '',
    contribution_frequency: chama.contribution_frequency || 'monthly',
    contribution_amount: chama.contribution_amount || '',
    meeting_day: chama.meeting_day || '',
    late_penalty: chama.late_penalty || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      <Card title="Basic Information">
        <div className="space-y-4">
          <Input
            label="Chama Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div>
            <div className="block text-sm font-medium text-gray-700 mb-2">Description</div>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
            />
          </div>
        </div>
      </Card>

      <Card title="Contribution Settings">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Contribution Amount (KES)"
              type="number"
              value={formData.contribution_amount}
              onChange={(e) => setFormData({ ...formData, contribution_amount: e.target.value })}
            />
            <div>
              <div className="block text-sm font-medium text-gray-700 mb-2">Frequency</div>
              <select
                value={formData.contribution_frequency}
                onChange={(e) =>
                  setFormData({ ...formData, contribution_frequency: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>
          <Input
            label="Late Penalty (KES)"
            type="number"
            value={formData.late_penalty}
            onChange={(e) => setFormData({ ...formData, late_penalty: e.target.value })}
          />
        </div>
      </Card>

      <div className="flex gap-4">
        <Button type="submit">Save Changes</Button>
        <Button type="button" variant="outline">
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ChamaDetailEnhanced;
